import { AppLayout, AuthLayout } from 'layouts';
import React, { Fragment, lazy, Suspense } from 'react';
import { RouteConfig } from 'react-router-config';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  AppGuard,
  AuthGuard,
  CEEAccessGuard,
  LeadReferentGuard,
} from 'routes/guards';

/** Guard composé : authentification + accès CEE (non Partenaire Lead seul). */
function AppCEEGuard({ children }: { children: React.ReactNode }) {
  return (
    <AppGuard>
      <CEEAccessGuard>{children}</CEEAccessGuard>
    </AppGuard>
  );
}

/** Guard composé : authentification + Partenaire Lead. */
function AppLeadGuard({ children }: { children: React.ReactNode }) {
  return (
    <AppGuard>
      <LeadReferentGuard>{children}</LeadReferentGuard>
    </AppGuard>
  );
}

import { LoadingScreen } from 'components';

export const routesConfig: RouteConfig[] = [
  {
    path: '/errors/error-404',
    exact: true,
    component: lazy(() => import('views/errors/404.page')),
    layout: Fragment,
    guard: Fragment,
  },
  {
    path: '/',
    exact: true,
    component: lazy(() => import('views/home/Home.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/gestion-leads/suivi-leads/:id',
    exact: true,
    component: lazy(() => import('views/gestion-leads/SuiviLeadDetail.page')),
    layout: AppLayout,
    guard: AppLeadGuard,
  },
  {
    path: '/gestion-leads/proposition-leads/:leadId',
    exact: true,
    component: lazy(() => import('views/gestion-leads/GestionLeads.page')),
    layout: AppLayout,
    guard: AppLeadGuard,
  },
  {
    path: ['/gestion-leads', '/gestion-leads/:tab'],
    exact: true,
    component: lazy(() => import('views/gestion-leads/GestionLeads.page')),
    layout: AppLayout,
    guard: AppLeadGuard,
  },
  {
    path: '/calculer-une-prime',
    exact: true,
    component: lazy(
      () => import('views/calculer-une-prime/CalculerUnePrime.page'),
    ),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/simulations',
    exact: true,
    component: lazy(() => import('views/simulations/SimulationsHistory.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/simulations/:simulationId',
    exact: true,
    component: lazy(() => import('views/simulations/SimulationDetails.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/tableau-de-bord',
    exact: true,
    component: lazy(() => import('views/tableau-de-bord/TableauDeBord.page')),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/generation-document',
    exact: true,
    component: lazy(
      () => import('views/generation-document/GenerationDocument.page'),
    ),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/dossiers',
    exact: true,
    component: lazy(() => import('views/dossiers/list/ListeDossier.page')),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/dossiers/:dossierId',
    exact: true,
    component: lazy(() => import('views/dossier/Dossier.page')),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/dossiers/:dossierId/:operationId',
    exact: true,
    component: lazy(() => import('views/dossier/Dossier.page')),
    layout: AppLayout,
    guard: AppCEEGuard,
  },
  {
    path: '/reinitialiser-mot-de-passe',
    exact: true,
    component: lazy(() => import('views/auth/UpdatePassword.page')),
    layout: AuthLayout,
    guard: AuthGuard,
  },
  {
    path: '/creation-compte',
    exact: true,
    component: lazy(() => import('views/auth/UpdatePassword.page')),
    layout: AuthLayout,
    guard: AuthGuard,
  },
  {
    path: '/connexion',
    exact: true,
    component: lazy(() => import('views/auth/Login.page')),
    layout: AuthLayout,
    guard: AuthGuard,
  },
  {
    path: '/impersonate',
    exact: true,
    component: lazy(() => import('views/auth/Impersonate.page')),
    layout: AuthLayout,
  },
  {
    path: '/mot-de-passe-oublie',
    exact: true,
    component: lazy(() => import('views/auth/ResetPasswordInit.page')),
    layout: AuthLayout,
    guard: AuthGuard,
  },
  {
    path: '/mon-compte',
    exact: true,
    component: lazy(() => import('views/mon-compte/monCompte.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/tableau-de-bord',
    exact: true,
    component: lazy(() => import('views/not-implemented/NotImplemented.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/faq',
    exact: true,
    component: lazy(() => import('views/faq/FAQ.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/mentions-legales',
    exact: true,
    component: lazy(() => import('views/not-implemented/NotImplemented.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/conditions-generales',
    exact: true,
    component: lazy(() => import('views/not-implemented/NotImplemented.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/politique-de-confidentialite',
    exact: true,
    component: lazy(() => import('views/not-implemented/NotImplemented.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/lien-non-valide',
    exact: true,
    component: lazy(() => import('views/auth/ResendLink.page')),
    layout: AuthLayout,
    guard: AuthGuard,
  },
  {
    path: '/support/new',
    exact: true,
    component: lazy(() => import('views/support/form/Support.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/support/:id',
    exact: true,
    component: lazy(() => import('views/support/detail/detail.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '/support',
    exact: true,
    component: lazy(() => import('views/support/accueil/SupportList.page')),
    layout: AppLayout,
    guard: AppGuard,
  },
  {
    path: '*',
    component: () => <Redirect to="/errors/error-404" />,
    layout: Fragment,
    guard: Fragment,
  },
];

const renderRoutes = (routes: RouteConfig[]) =>
  routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route: RouteConfig, i: number) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component: any = route.component;
          // `Fragment` n'accepte que `key`/`children` : ne pas lui passer de className.
          const layoutProps =
            Layout === Fragment ? {} : { className: 'min-h-screen' };
          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard>
                  <Layout {...layoutProps}>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
